export default (klass) => class ThreeMinStepTest extends klass {

  static targets = [
    ...super.targets,
    'threeMinStepTest',
    'threeMinStepTestReadingsInput',
    'threeMinStepTestReadings',
    'threeMinStepTestConsent',
    'threeMinStepTestConsentInput',
    'threeMinStepTestContraindicationsNoneOfTheAbove',
    'threeMinStepTestContraindicationsWarnings',
    'diastolicAtRest',
    'pulseAtRest',
    'pulseEscalation',
    'systolicAtRest',
    'threeMinStepTestCompletion',
    'threeMinStepTestCompletionInverse',
    'threeMinStepTestEscalation',
    'threeMinStepTestEscalationWarnings',
    'threeMinStepTestPerform'
  ]

  static values = {
    ...super.values,
    dclValidity:      Boolean,
    escalationRanges: Object
  }

  connect() {
    super.connect()
    this.fetchEscalationRanges()
  }

  initialize() {
    super.initialize()
    this.diastolicAtRestMax   = null
    this.elevatedBpMessage    = null
    this.elevatedPulseMessage = null
    this.pulseAtRestMax       = null
    this.systolicAtRestMax    = null
  }

  toggleThreeMinStepTest({ currentTarget }) {
    this.toggleCheckboxMoreDetails({
      input:  currentTarget,
      target: this.threeMinStepTestTarget
    })
  }

  validReadingInputs() {
    return this.threeMinStepTestReadingsInputTargets.every((input) => input.value !== '')
  }

  // === Escalation

  fetchEscalationRanges() {
    const escalationRangeData = this.escalationRangesValue

    this.diastolicAtRestMax   = escalationRangeData.diastolic_at_rest_max
    this.elevatedBpMessage    = escalationRangeData.elevated_bp_message
    this.elevatedPulseMessage = escalationRangeData.elevated_pulse_message
    this.pulseAtRestMax       = escalationRangeData.pulse_at_rest_max
    this.systolicAtRestMax    = escalationRangeData.systolic_at_rest_max
  }

  // === Toggles

  toggleThreeMinStepTestContraindicationsNoneOfTheAbove({ currentTarget }) {
    this.toggleCheckboxNoneOfTheAbove({
      input:         currentTarget,
      targets:       this.threeMinStepTestContraindicationsNoneOfTheAboveTargets,
      dispatchInput: false
    })

    this.#toggleThreeMinStepTestReadings(
      this.threeMinStepTestContraindicationsNoneOfTheAboveTargets
    )
  }

  toggleThreeMinStepTestEscalation({ currentTarget }) {
    if (currentTarget.value === '') return
    if (this.systolicAtRestValue === 0 || this.diastolicAtRestValue === 0) return

    const elevatedBpAtRest       = this.#isElevatedBP()
    const elevatedPulseAtRest    = this.#isElevatedPulse()
    const elevatedReadingsAtRest = (elevatedBpAtRest || elevatedPulseAtRest)

    if (elevatedReadingsAtRest) this.warningMessage = this.#i18nMessage(elevatedBpAtRest, elevatedPulseAtRest)

    this.toggleTarget(this.threeMinStepTestConsentTarget, !elevatedReadingsAtRest)
    this.toggleTarget(this.threeMinStepTestPerformTarget, this.threeMinStepTestConsentInputTarget.checked && !elevatedReadingsAtRest)
    this.toggleTarget(this.threeMinStepTestEscalationWarningsTarget, elevatedReadingsAtRest)
  }

  toggleThreeMinStepTestPerform({ currentTarget: { checked } }) {
    this.toggleTarget(this.threeMinStepTestPerformTarget, checked)
  }

  toggleThreeMinStepTestCompletion({ currentTarget: { checked } }) {
    this.toggleTarget(this.threeMinStepTestCompletionInverseTarget, checked)
    this.toggleTarget(this.threeMinStepTestCompletionTarget, !checked)
  }

  // ==== Getters

  get systolicAtRestValue() {
    const value = parseInt(this.systolicAtRestTarget.value, 10)
    return Number.isNaN(value) ? 0 : value
  }

  get diastolicAtRestValue() {
    const value = parseInt(this.diastolicAtRestTarget.value, 10)
    return Number.isNaN(value) ? 0 : value
  }

  get pulseAtRestValue() {
    const value = parseInt(this.pulseAtRestTarget.value, 10)
    return Number.isNaN(value) ? 0 : value
  }

  // ==== Setters

  /**
   * @param {any} message
   */
  set warningMessage(message) {
    this.threeMinStepTestEscalationWarningsTarget.querySelector('p').innerHTML = message
  }

  #toggleThreeMinStepTestReadings(...targetGroups) {
    const showReadings = targetGroups.every((targets) => {
      const noneCheckbox = targets.find((target) => target.value === 'none_of_the_above')
      if (!noneCheckbox) throw new Error(`missing none_of_the_above checkbox: ${targets[0]?.name || 'unknown'}`)
      if (this.dclValidityValue) return noneCheckbox.checked

      return noneCheckbox.checked || targets.every((target) => !target.checked)
    })
    const showWarnings = targetGroups.some((targets) => targets.some((target) => target.checked && target.value !== 'none_of_the_above'))

    this.threeMinStepTestReadingsTarget.classList.toggle(this.hideClass, !showReadings)
    this.threeMinStepTestContraindicationsWarningsTarget.classList.toggle(this.hideClass, this.dclValidityValue ? !showWarnings : showReadings)
  }

  #isElevatedBP() {
    return this.systolicAtRestValue > this.systolicAtRestMax || this.diastolicAtRestValue > this.diastolicAtRestMax
  }

  #isElevatedPulse() {
    return this.pulseAtRestValue > this.pulseAtRestMax
  }

  #i18nMessage(elevatedBpAtRest, elevatedPulse) {
    if (elevatedPulse && !elevatedBpAtRest) return this.elevatedPulseMessage

    return this.elevatedBpMessage
  }

}
